<script setup lang="ts">
import FileUpload from "~/components/Common/Upload/FileUpload.vue";
import GlobalModal from "~/components/Common/Modals/GlobalModal.vue";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { ItemType } from "~/utils/enums/common.enums";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useAuthStore } from "~/store/auth/auth.store";
import { useDocumentsStore } from "~/store/documents/documents.store";
import Tag from "~/components/Common/Tag.vue";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import SessionRemoveDocumentPopup from "../session-details/tabs/documents/SessionRemoveDocumentPopup.vue";
import { useSessionDetailsDocumentsStore } from "~/store/sessions/session-details-documents-store";
import AppButton from "~/components/Common/Buttons/AppButton.vue";

const { t } = useI18n();

const auth = useAuthStore();
const documentsStore = useDocumentsStore();
const sessionStore = useSessionsStore();
const { trainingPlanSessionIndividual } = storeToRefs(sessionStore);
const sessionDetailsStore = useSessionDetailsStore();
const sessionDetailsDocumentsStore = useSessionDetailsDocumentsStore();
const { showSessionRemoveDocumentModal } = storeToRefs(sessionDetailsDocumentsStore);
const uploadModal = ref(false);
const feedback = useFeedback();
const toggleShowMore = ref<boolean>(false);
const documentIdToRemove = ref<string>("");

const itemSession = () => {
  const itemId = trainingPlanSessionIndividual.value?.bookingId;
  const itemType = ItemType.BOOKING;
  return { itemType, itemId };
};

const nbElements = computed(() => {
  return toggleShowMore.value ? trainingPlanSessionIndividual.value?.documents?.length : 3;
});

const hide = async () => {
  uploadModal.value = false;
  if (trainingPlanSessionIndividual.value?.id) {
    await sessionStore.fetchTrainingPlanSession(trainingPlanSessionIndividual.value?.id);
  }
};

const downloadDocument = async (documentId: string) => {
  const downloaded = await documentsStore.downloadDocument(documentId);
  if (downloaded) {
    const link = document.createElement("a");
    link.href = downloaded.documentLink;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    feedback.error(`Une erreur est survenue`, "small");
  }
};

function fileUploaded() {
  if (sessionDetailsStore.sessionDetails?.id) {
    sessionDetailsDocumentsStore.fetchSessionDocumentsWithPagination(sessionDetailsStore.sessionDetails.id, {
      limit: 10000,
    });
  }
}

/**
 * Show remove document modal and set documentIdToRemove
 *
 * @param documentId DocumentId to remove
 */
async function removeDocument(documentId: string) {
  documentIdToRemove.value = documentId;
  showSessionRemoveDocumentModal.value = true;
}

/**
 * Switch toggleShowMore to false once new documents are uploaded
 */
function resetToggleShowMore() {
  toggleShowMore.value = false;
}

function removeDocumentFromList() {
  if (trainingPlanSessionIndividual.value?.id) {
    sessionStore.fetchTrainingPlanSession(trainingPlanSessionIndividual.value?.id);
  }
}
</script>

<template>
  <div class="session_upload__container">
    <h3 class="heading_3_demi text-primary-color p-[8px]">Document</h3>
    <div class="text-center px-[18px] mt-[24px]">
      <table
        v-if="
          trainingPlanSessionIndividual &&
          trainingPlanSessionIndividual.documents &&
          trainingPlanSessionIndividual.documents.length
        "
        class="table-auto border-separate border-spacing-y-[4px] w-full mb-[24px]"
      >
        <tbody class="pb-[32px] mb-[32px]">
          <tr
            v-for="document in trainingPlanSessionIndividual.documents.slice(0, nbElements)"
            :key="document.documentId"
          >
            <td class="text-start pr-[16px] w-[45%]">
              <span
                class="text-start cursor-pointer underline body_regular break-words w-[472px] !text-ellipsis !overflow-hidden"
                @click="downloadDocument(document.documentId)"
                >{{ document.fileName }}</span
              >
            </td>
            <td class="text-start justify-start pr-[16px] w-fit">
              <Tag
                v-if="document.documentType"
                class="w-fit"
                :tag="{
                  value: t(`session.${document.documentType}`),
                  type: ColorsTypeEnum.GREY,
                  logo: 'document',
                }"
              ></Tag>
            </td>
            <td v-if="document.createdBy === auth.user?.userId" class="items-end">
              <SvgIcon
                name="delete_trash"
                class="ml-auto color_trash cursor-pointer h-[16px] w-[16px]"
                @click="removeDocument(document.documentId)"
              ></SvgIcon>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="trainingPlanSessionIndividual?.documents && trainingPlanSessionIndividual?.documents?.length > 3"
        class="flex justify-center"
      >
        <button class="px-[22px] underline show_more body_regular" @click="toggleShowMore = !toggleShowMore">
          {{
            !toggleShowMore ? `Afficher plus (${trainingPlanSessionIndividual?.documents.length})` : "Afficher moins"
          }}
        </button>
      </div>

      <AppButton
        type="outlined"
        label="Nouveau document"
        icon="add"
        class="w-full max-w-[448px] mt-[24px]"
        @click="uploadModal = true"
      />
    </div>
  </div>
  <GlobalModal v-if="uploadModal">
    <template #body>
      <HeaderDetails back-icon="arrow_left" :close="false" back @backClick="uploadModal = false">
        <template #page>Importer des documents</template>
      </HeaderDetails>
      <FileUpload
        class="mt-[16px]"
        :document-type="'other'"
        :item-type="'other'"
        :item-id="itemSession().itemId || ''"
        :booking-details="true"
        @hide="hide()"
        @fileUploaded="fileUploaded()"
        @resetToggleShowMore="resetToggleShowMore()"
      ></FileUpload>
    </template>
  </GlobalModal>

  <SessionRemoveDocumentPopup
    v-if="showSessionRemoveDocumentModal"
    :document-id="documentIdToRemove"
    @resetToggleShowMore="removeDocumentFromList()"
  ></SessionRemoveDocumentPopup>
</template>

<style lang="scss">
@use "assets/scss/texts";

.session_upload__svg {
  @apply w-[16px] h-[16px];
}

.session_upload__container {
  @apply mt-[16px] pb-[32px];
  border-bottom: 1px solid var(--grey-lightest);
}

.session_details__body {
  @apply grid gap-[24px];
  max-width: 944px;
  width: 100%;

  h1 {
    @extend .heading_1;
  }

  h3 {
    @extend .heading_3_demi;
    @apply p-[8px];
  }
}
</style>
